import { KAMINO_EMIT_PF_IDS } from '../constants/oikosReceitas';
import powerappsApi from '../services/powerapps';

const powerappsMultipart = {
  headers: {
    'Content-Type': 'multipart/form-data',
    Accept: 'multipart/form-data',
  },
};

export function canEmitNfse(user) {
  return KAMINO_EMIT_PF_IDS.includes(Number(user?.pf_id));
}

/**
 * @returns {'nao_enviado'|'aguardando'|'sincronizado'}
 */
export function getKaminoEmitStatus(rec) {
  const kaminoId = Number(rec?.kamino_id) || 0;
  const nfId = Number(rec?.nf_id) || 0;
  const fat = Number(rec?.rec_fat) === 1;
  if (nfId > 0 || fat) return 'sincronizado';
  if (kaminoId > 0) return 'aguardando';
  return 'nao_enviado';
}

export function kaminoStatusLabel(status) {
  if (status === 'aguardando') return 'Aguardando emissão';
  if (status === 'sincronizado') return 'Sincronizada';
  return 'Não enviado';
}

/**
 * Lista o que impede o envio (campos vazios, PJ sem kamino_id, gates de negócio).
 * @returns {string[]}
 */
export function getEmitMissingFields(rec) {
  const missing = [];
  if (Number(rec?.rec_aut) !== 1) missing.push('Faturamento não liberado');
  if (Number(rec?.rec_fat) === 1) missing.push('Receita já faturada');
  if (!String(rec?.rec_NFDescricao || '').trim()) missing.push('Descrição da NF');
  const leftoverChaves = [...String(rec?.rec_NFDescricao || '').matchAll(/\{([^}]+)\}/g)].map((m) => `{${m[1]}}`);
  if (leftoverChaves.length) {
    missing.push(`Descrição da NF ainda tem ${leftoverChaves.join(', ')}`);
  }
  if (rec?.kamino_imposto == null || String(rec.kamino_imposto).trim() === '') {
    missing.push('Tabela de imposto');
  }
  if (!String(rec?.contrato_codTrib || '').trim()) missing.push('Código de serviço');
  if (!(Number(rec?.mun_id) > 0)) missing.push('Município');
  if (!rec?.pjTomadorKaminoId) missing.push('Tomador sem ID Kamino');
  if (!rec?.pjPrestadorKaminoId) missing.push('Prestador sem ID Kamino');
  return missing;
}

/** Opções padrão de lista de serviços (IDs já usados no Oikos). */
export const DEFAULT_COD_TRIB_OPTIONS = [
  { value: '170901', label: '170901' },
  { value: '170101', label: '170101' },
];

/**
 * Lista tabelas de imposto ativas na Kamino via PowerApps API.
 * @returns {Promise<Array<{ value: string|number, label: string, raw: object }>>}
 */
export async function listarTabelasImposto() {
  const res = await powerappsApi.post(
    '/',
    { acao: 'kamino_listar_tabelas_imposto' },
    powerappsMultipart,
  );
  const payload = res?.data;
  if (!payload || payload.ok !== true || !Array.isArray(payload.data)) {
    throw new Error(payload?.message || 'Falha ao listar tabelas de imposto na Kamino.');
  }
  return payload.data
    .filter((row) => row && (row.Ativo === true || row.Ativo === 1 || row.Ativo == null))
    .map((row) => ({
      value: String(row.ID),
      label: `${row.ID} — ${row.Nome || 'Sem nome'}`,
      raw: row,
    }));
}

export async function lerNfseKamino({ id }) {
  const res = await powerappsApi.post(
    '/',
    { acao: 'kamino_ler_nfse', id: Number(id) },
    powerappsMultipart,
  );
  const payload = res?.data;
  if (!payload || payload.ok !== true) {
    throw new Error(payload?.message || 'Falha ao consultar a NFS-e na Kamino.');
  }
  return payload;
}

export async function emitNfse({ rec_id, rec_ids } = {}) {
  const body = { acao: 'kamino_emit_nfse', rec_id: Number(rec_id) };
  if (Array.isArray(rec_ids) && rec_ids.length) {
    body.rec_ids = rec_ids.map((id) => Number(id)).filter((id) => id > 0);
  }
  const res = await powerappsApi.post(
    '/',
    body,
    powerappsMultipart,
  );
  const payload = res?.data;
  if (!payload || payload.ok !== true) {
    const msg = payload?.message || 'Falha ao emitir NFS-e.';
    const err = new Error(msg);
    err.payload = payload;
    throw err;
  }
  return payload;
}


export async function enviarEmailNfseTeste({ rec_id }) {
  const res = await powerappsApi.post(
    '/',
    { acao: 'kamino_enviar_email_nfse', rec_id: Number(rec_id) },
    powerappsMultipart,
  );
  const payload = res?.data;
  if (!payload || payload.ok !== true) {
    const st = (payload && payload.email && payload.email.status) || payload?.message || 'Falha ao enviar e-mail da NFS-e.';
    const extra = payload && payload.email && payload.email.error ? `: ${payload.email.error}` : '';
    throw new Error(`${st}${extra}`);
  }
  return payload;
}

export async function checkPendingNfse({ limit = 50, rec_id = null } = {}) {
  const body = { acao: 'kamino_check_nfse', limit: Number(limit) || 50 };
  if (Number(rec_id) > 0) body.rec_id = Number(rec_id);
  const res = await powerappsApi.post(
    '/',
    body,
    powerappsMultipart,
  );
  const payload = res?.data;
  if (!payload || typeof payload !== 'object') {
    return { processed: 0, skipped: 0, imported: [], errors: [] };
  }
  if (payload.ok === false) {
    throw new Error(payload.message || 'Falha ao sincronizar NFS-e Kamino.');
  }
  return payload;
}

export async function sugerirPacotesKaminoMes({ de, ate }) {
  const res = await powerappsApi.post(
    '/',
    { acao: 'kamino_sugerir_pacotes_mes', de, ate },
    powerappsMultipart,
  );
  const payload = res?.data;
  if (!payload || payload.ok !== true) {
    throw new Error(payload?.message || 'Falha ao mapear as notas da Kamino.');
  }
  return payload;
}
